"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import {
  AccountBadge,
  Crumbs,
  Page,
  ToggleChip,
} from "@/components/ui";
import { HttpError, PortfolioSummaryRow, api } from "@/lib/api";
import { fmtCycleLabel, fmtPct, fmtRupeesShort, pnlClass } from "@/lib/fmt";
import { isIncompleteAccount } from "@/lib/dataQuality";

const TWR_EXPLAINER =
  "Arithmetic Time-Weighted Return — Σ (daily_pnl / margin_in_force_that_day) × 100. " +
  "Bump-aware because the denominator is per-day, so mid-cycle margin changes are " +
  "reflected honestly. On constant margin it collapses to total_pnl / margin — the " +
  "true return on capital deployed for margin-based derivatives (no compounding uplift).";

// Sticky first column + horizontal scroll: raw <table> with tailwind
// classes so we can put `sticky left-0 bg-background` on the leftmost
// cells (Th/Td primitives don't expose className on <th>).

export default function PortfolioSummaryPage() {
  const [rows, setRows] = useState<PortfolioSummaryRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [activeOnly, setActiveOnly] = useState(true);

  useEffect(() => {
    setErr(null);
    setRows(null);
    api.portfolioSummary(activeOnly)
      .then(setRows)
      .catch((e) => {
        if (e instanceof HttpError && e.status === 403) setErr("Admin-only report.");
        else setErr(String(e));
      });
  }, [activeOnly]);

  // Pivot server-side long format → grid shape client-side.
  // - Accounts sorted by cum_pnl_net_rs at their latest cycle (desc).
  // - Cycles sorted by cycle_start desc (recent first).
  const grid = useMemo(() => {
    if (!rows || rows.length === 0) return null;

    // Cycles — unique + ordered by cycle_start desc
    const cycleMap = new Map<string, { label: string; start: string; end: string }>();
    for (const r of rows) {
      if (!cycleMap.has(r.cycle_label)) {
        cycleMap.set(r.cycle_label, {
          label: r.cycle_label,
          start: r.cycle_start,
          end: r.cycle_end,
        });
      }
    }
    const cycles = [...cycleMap.values()].sort((a, b) => b.start.localeCompare(a.start));

    // Per-account latest-cycle metrics — drive column order (by cum_pnl
    // desc) and the Σ-lifetime footer + grand totals.
    const latestPerAccount = new Map<
      string,
      {
        account: string; account_id: number; broker: string;
        is_client_account: boolean; clientcode: string | null;
        cum: number;                       // cum_pnl_net_rs
        cumReturnTwrPct: number | null;    // cum_return_twr_pct at latest cycle
        capital: number | null;            // capital_rs at latest cycle (portfolio return denom)
      }
    >();
    for (const c of cycles) {
      for (const r of rows) {
        if (r.cycle_label !== c.label) continue;
        if (!latestPerAccount.has(r.account)) {
          latestPerAccount.set(r.account, {
            account: r.account, account_id: r.account_id, broker: r.broker,
            is_client_account: r.is_client_account, clientcode: r.clientcode,
            cum: Number(r.cum_pnl_net_rs),
            cumReturnTwrPct: r.cum_return_twr_pct === null || r.cum_return_twr_pct === undefined
              ? null : Number(r.cum_return_twr_pct),
            capital: r.capital_rs === null || r.capital_rs === undefined
              ? null : Number(r.capital_rs),
          });
        }
      }
    }
    const accounts = [...latestPerAccount.values()].sort((a, b) => b.cum - a.cum);

    // Pivot cell lookups
    const cells        = new Map<string, number>();               // (cycle::acct) → pnl_net_rs
    const returnCells  = new Map<string, number | null>();        // (cycle::acct) → cycle_return_twr_pct
    const capitalCells = new Map<string, number | null>();        // (cycle::acct) → capital_rs
    for (const r of rows) {
      const key = `${r.cycle_label}::${r.account}`;
      cells.set(key, Number(r.pnl_net_rs));
      returnCells.set(
        key,
        r.cycle_return_twr_pct === null || r.cycle_return_twr_pct === undefined
          ? null : Number(r.cycle_return_twr_pct),
      );
      capitalCells.set(
        key,
        r.capital_rs === null || r.capital_rs === undefined
          ? null : Number(r.capital_rs),
      );
    }

    // Row totals — pnl side.
    const rowTotals = new Map<string, number>();
    // Row totals — return side. Portfolio-weighted pool:
    //     Σ pnl_net_rs / Σ capital_rs × 100
    // across visible accounts. Yields NaN → null when no account has a
    // non-null capital for that cycle.
    const returnRowTotals = new Map<string, number | null>();
    for (const c of cycles) {
      let pnlSum = 0;
      let capSum = 0;
      let capHits = 0;
      for (const a of accounts) {
        const p = cells.get(`${c.label}::${a.account}`);
        if (p !== undefined) pnlSum += p;
        const cap = capitalCells.get(`${c.label}::${a.account}`);
        if (cap !== undefined && cap !== null && cap > 0) {
          capSum += cap;
          capHits += 1;
        }
      }
      rowTotals.set(c.label, pnlSum);
      returnRowTotals.set(c.label, capHits > 0 && capSum > 0 ? (pnlSum / capSum) * 100 : null);
    }
    const grand = accounts.reduce((s, a) => s + a.cum, 0);
    // Grand portfolio return — Σ cum_pnl_net_rs / Σ latest-capital × 100
    let grandCap = 0;
    let grandCapHits = 0;
    for (const a of accounts) {
      if (a.capital !== null && a.capital > 0) {
        grandCap += a.capital;
        grandCapHits += 1;
      }
    }
    const grandReturn = grandCapHits > 0 && grandCap > 0 ? (grand / grandCap) * 100 : null;

    return {
      cycles, accounts,
      cells, rowTotals, grand,
      returnCells, returnRowTotals, grandReturn,
    };
  }, [rows]);

  function downloadCsv(csv: string, filename: string) {
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const el = document.createElement("a");
    el.href = url;
    el.download = filename;
    document.body.appendChild(el);
    el.click();
    document.body.removeChild(el);
    URL.revokeObjectURL(url);
  }

  function exportPnlCsv() {
    if (!grid) return;
    const { cycles, accounts, cells, rowTotals, grand } = grid;
    const header = ["Cycle", ...accounts.map((a) => a.account), "Row total"].join(",");
    const lines = cycles.map((c) => {
      const cells_ = accounts.map((a) => {
        const v = cells.get(`${c.label}::${a.account}`);
        return v === undefined ? "" : String(v);
      });
      return [c.label, ...cells_, String(rowTotals.get(c.label) ?? 0)].join(",");
    });
    const footer = ["Lifetime", ...accounts.map((a) => String(a.cum)), String(grand)].join(",");
    const csv = [header, ...lines, footer].join("\n");
    downloadCsv(csv, `portfolio-summary-pnl-${new Date().toISOString().slice(0, 10)}.csv`);
  }

  function exportReturnsCsv() {
    if (!grid) return;
    const { cycles, accounts, returnCells, returnRowTotals, grandReturn } = grid;
    const header = ["Cycle", ...accounts.map((a) => a.account), "Row total"].join(",");
    const lines = cycles.map((c) => {
      const cells_ = accounts.map((a) => {
        const v = returnCells.get(`${c.label}::${a.account}`);
        return v === undefined || v === null ? "" : String(v);
      });
      const rt = returnRowTotals.get(c.label);
      return [c.label, ...cells_, rt === null || rt === undefined ? "" : String(rt)].join(",");
    });
    const footer = [
      "Lifetime",
      ...accounts.map((a) => a.cumReturnTwrPct === null ? "" : String(a.cumReturnTwrPct)),
      grandReturn === null ? "" : String(grandReturn),
    ].join(",");
    const csv = [header, ...lines, footer].join("\n");
    downloadCsv(csv, `portfolio-summary-returns-${new Date().toISOString().slice(0, 10)}.csv`);
  }

  if (err) return <p className="text-sm text-down">{err}</p>;
  if (!rows) return <p className="eyebrow text-mute">Loading…</p>;

  // Hairline row styling — matches the shared DataTable / data-freshness look
  // (Thead: border-b border-rule; row cells: border-b border-rule/50; no
  // vertical gridlines). border-separate + border-spacing-0 is retained
  // because the sticky first column needs it (see <Table sticky> in ui.tsx);
  // per-cell border-b then draws the row hairlines cleanly.
  const CELL_BASE = "py-1.5 pr-4 text-sm num whitespace-nowrap border-b border-rule/50";
  const HEADER_BASE = "py-1.5 pr-4 eyebrow font-normal text-mute whitespace-nowrap border-b border-rule";

  return (
    <Page
      eyebrow={<Crumbs items={[{ label: "Reports" }, { label: "Portfolio summary (Aggregate)" }]} />}
      title={<>Portfolio <em className="italic">summary</em> (Aggregate)</>}
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <ToggleChip
          selected={activeOnly}
          onClick={() => setActiveOnly((v) => !v)}
        >
          Active only
        </ToggleChip>
        <button
          type="button"
          onClick={exportPnlCsv}
          disabled={!grid}
          className="eyebrow text-mute transition hover:text-foreground disabled:opacity-40"
        >
          ↳ Export P&amp;L CSV
        </button>
        <button
          type="button"
          onClick={exportReturnsCsv}
          disabled={!grid}
          className="eyebrow text-mute transition hover:text-foreground disabled:opacity-40"
        >
          ↳ Export Returns CSV
        </button>
        <div className="ml-auto eyebrow text-mute">
          {grid ? `${grid.cycles.length} cycles × ${grid.accounts.length} accounts` : ""}
        </div>
      </div>

      {!grid ? (
        <p className="eyebrow text-mute">No cycles found.</p>
      ) : (
        <>
          <div className="mb-2 eyebrow text-mute">P&amp;L (₹)</div>
          <div className="overflow-x-auto">
          <table className="table-fixed border-separate border-spacing-0">
            <colgroup>
              <col style={{ width: "11rem" }} />
              {grid.accounts.map((a) => (
                <col key={a.account} style={{ width: "7rem" }} />
              ))}
              <col style={{ width: "7rem" }} />
            </colgroup>
            <thead>
              <tr>
                <th className={`${HEADER_BASE} text-left sticky left-0 bg-background z-10`}>
                  NIFTY expiry cycle
                </th>
                {grid.accounts.map((a) => (
                  <th key={a.account} className={`${HEADER_BASE} text-right`}>
                    <div className="flex flex-col items-end gap-1">
                      <Link
                        href={`/accounts/${a.account_id}`}
                        className="hover:text-foreground"
                      >
                        {a.account}
                      </Link>
                      <AccountBadge
                        is_client_account={a.is_client_account}
                        clientcode={a.clientcode} accountId={a.account_id}
                      />
                      {/* #135 — incomplete-data marker, this column only */}
                      {isIncompleteAccount(a.account) && (
                        <span
                          className="text-[10px] font-normal tracking-wide text-down"
                          title="Some active trading days are missing for this account — figures may be incomplete."
                        >
                          ⚠ incomplete
                        </span>
                      )}
                    </div>
                  </th>
                ))}
                <th className={`${HEADER_BASE} text-right`}>Σ row</th>
              </tr>
            </thead>
            <tbody>
              {grid.cycles.map((c) => (
                <tr key={c.label}>
                  <th
                    scope="row"
                    className={`${CELL_BASE} text-left font-normal text-mute sticky left-0 bg-background`}
                  >
                    {fmtCycleLabel(c.label)}
                  </th>
                  {grid.accounts.map((a) => {
                    const v = grid.cells.get(`${c.label}::${a.account}`);
                    return (
                      <td
                        key={a.account}
                        className={`${CELL_BASE} text-right ${
                          v === undefined ? "text-mute" : pnlClass(v)
                        }`}
                      >
                        {v === undefined ? "—" : fmtRupeesShort(v)}
                      </td>
                    );
                  })}
                  <td
                    className={`${CELL_BASE} text-right ${pnlClass(
                      grid.rowTotals.get(c.label) ?? 0,
                    )}`}
                  >
                    {fmtRupeesShort(grid.rowTotals.get(c.label) ?? 0)}
                  </td>
                </tr>
              ))}
              <tr>
                <th
                  scope="row"
                  className={`${CELL_BASE} text-left font-semibold sticky left-0 bg-background`}
                >
                  Σ lifetime
                </th>
                {grid.accounts.map((a) => (
                  <td
                    key={a.account}
                    className={`${CELL_BASE} text-right font-semibold ${pnlClass(a.cum)}`}
                  >
                    {fmtRupeesShort(a.cum)}
                  </td>
                ))}
                <td className={`${CELL_BASE} text-right font-semibold ${pnlClass(grid.grand)}`}>
                  {fmtRupeesShort(grid.grand)}
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        </>
      )}

      {grid && (
        <>
          <div className="mt-8 mb-2 eyebrow text-mute">
            Time-Weighted Return (
            <abbr
              title={TWR_EXPLAINER}
              className="cursor-help underline decoration-dotted decoration-mute underline-offset-2"
            >
              TWR
            </abbr>
            ) %
          </div>
          <div className="overflow-x-auto">
            <table className="table-fixed border-separate border-spacing-0">
              <colgroup>
                <col style={{ width: "11rem" }} />
                {grid.accounts.map((a) => (
                  <col key={a.account} style={{ width: "7rem" }} />
                ))}
                <col style={{ width: "7rem" }} />
              </colgroup>
              <thead>
                <tr>
                  <th className={`${HEADER_BASE} text-left sticky left-0 bg-background z-10`}>
                    NIFTY expiry cycle
                  </th>
                  {grid.accounts.map((a) => (
                    <th key={a.account} className={`${HEADER_BASE} text-right`}>
                      <Link
                        href={`/accounts/${a.account_id}`}
                        className="hover:text-foreground"
                      >
                        {a.account}
                      </Link>
                    </th>
                  ))}
                  <th className={`${HEADER_BASE} text-right`}>Σ row</th>
                </tr>
              </thead>
              <tbody>
                {grid.cycles.map((c) => (
                  <tr key={c.label}>
                    <th
                      scope="row"
                      className={`${CELL_BASE} text-left font-normal text-mute sticky left-0 bg-background`}
                    >
                      {fmtCycleLabel(c.label)}
                    </th>
                    {grid.accounts.map((a) => {
                      const v = grid.returnCells.get(`${c.label}::${a.account}`);
                      return (
                        <td
                          key={a.account}
                          className={`${CELL_BASE} text-right ${
                            v === undefined || v === null ? "text-mute" : pnlClass(v)
                          }`}
                        >
                          {v === undefined || v === null ? "—" : fmtPct(v)}
                        </td>
                      );
                    })}
                    <td
                      className={`${CELL_BASE} text-right ${
                        grid.returnRowTotals.get(c.label) === null ? "text-mute"
                          : pnlClass(grid.returnRowTotals.get(c.label) ?? 0)
                      }`}
                    >
                      {grid.returnRowTotals.get(c.label) === null
                        ? "—"
                        : fmtPct(grid.returnRowTotals.get(c.label) ?? 0)}
                    </td>
                  </tr>
                ))}
                <tr>
                  <th
                    scope="row"
                    className={`${CELL_BASE} text-left font-semibold sticky left-0 bg-background`}
                  >
                    Σ lifetime
                  </th>
                  {grid.accounts.map((a) => (
                    <td
                      key={a.account}
                      className={`${CELL_BASE} text-right font-semibold ${
                        a.cumReturnTwrPct === null ? "text-mute" : pnlClass(a.cumReturnTwrPct)
                      }`}
                    >
                      {a.cumReturnTwrPct === null ? "—" : fmtPct(a.cumReturnTwrPct)}
                    </td>
                  ))}
                  <td
                    className={`${CELL_BASE} text-right font-semibold ${
                      grid.grandReturn === null ? "text-mute" : pnlClass(grid.grandReturn)
                    }`}
                  >
                    {grid.grandReturn === null ? "—" : fmtPct(grid.grandReturn)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </Page>
  );
}
